// Tabs of a page or a card (DESIGN.md «Контролы»): arrows, Home and End move between tabs and open them, the line under the open tab
// slides to the next one. All tabs are shown while they fit the row; when they do not, the ones that do not fit go under «Ещё» at the
// end (um/2026-10-31, img-18), and a chosen hidden tab takes the last visible place. Measured before paint, so the row does not blink.

import { useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { cx } from './base.tsx';
import { Count, TabTrigger, stepKey, useMarker } from './controls.tsx';
import { Menu, MenuContent, MenuItem, MenuTrigger } from './menus.tsx';
import { fitTabs } from './tabs-fit.ts';
import s from './controls.module.css';

export interface TabItem<K extends string> { key: K; label: ReactNode; count?: number }

/**
 * `children` — things after the tabs in the same row. `overflow` — `more` (by default): tabs that do not fit go under «Ещё»;
 * `scroll`: the row scrolls sideways instead.
 */
export function Tabs<K extends string>({ value, onChange, items, label, bar, className, children, overflow = 'more' }: {
  value: K; onChange: (k: K) => void; items: TabItem<K>[]; label: string; className?: string; children?: ReactNode;
  /** The row of tabs on top of a pane: the pane's inner padding and surface. */
  bar?: boolean;
  overflow?: 'more' | 'scroll';
}) {
  const list = useRef<HTMLDivElement>(null);
  const probe = useRef<HTMLDivElement>(null);
  const keys = items.map((t) => t.key);
  const [fit, setFit] = useState<{ shown: K[]; hidden: K[] }>({ shown: keys, hidden: [] });
  const folds = overflow === 'more';
  const sig = items.map((t) => `${t.key}:${typeof t.label === 'string' ? t.label : ''}:${t.count ?? ''}`).join('|');
  // The probe row holds every tab and «Ещё» unseen: their widths decide what the visible row shows, at any width of the row.
  useLayoutEffect(() => {
    if (!folds || !list.current || !probe.current) return;
    const measure = () => {
      const el = list.current, pr = probe.current;
      if (!el || !pr) return;
      const cs = getComputedStyle(el);
      const avail = el.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight) - (children ? (el.querySelector<HTMLElement>('[data-tabs-after]')?.offsetWidth ?? 0) : 0);
      const cells = [...pr.children] as HTMLElement[];
      const widths = cells.slice(0, keys.length).map((c) => c.offsetWidth);
      const more = cells[keys.length]?.offsetWidth ?? 0;
      const next = fitTabs(keys, widths, more, parseFloat(cs.columnGap) || 0, avail, value);
      setFit((cur) => (cur.shown.join() === next.shown.join() && cur.hidden.join() === next.hidden.join() ? cur : next));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(list.current);
    return () => ro.disconnect();
  }, [folds, sig, value]);

  const byKey = new Map(items.map((t) => [t.key, t]));
  const shown = folds ? fit.shown.filter((k) => byKey.has(k)) : keys;
  const hidden = folds ? fit.hidden.filter((k) => byKey.has(k)) : [];
  const mark = useMarker(list, '[aria-selected="true"]', [value, shown.join()]);
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || !(e.target as HTMLElement).matches('[role="tab"]')) return;
    const next = stepKey(keys, value, e.key);
    if (next === null) return;
    e.preventDefault();
    onChange(next);
    requestAnimationFrame(() => list.current?.querySelector<HTMLElement>(`[data-tab="${next}"]`)?.focus());
  };
  const body = (t: TabItem<K>) => <>{t.label}{t.count !== undefined && <Count value={t.count} className={s.tabCount} />}</>;
  return (
    <div ref={list} role="tablist" aria-label={label} className={cx(s.tabs, folds && s.tabsFit, bar && s.tabsBar, className)} onKeyDown={onKey}>
      {shown.map((k) => { const t = byKey.get(k)!; return (
        <button key={t.key} type="button" role="tab" data-tab={t.key} aria-selected={t.key === value} tabIndex={t.key === value ? 0 : -1}
          className={s.tab} onClick={() => onChange(t.key)}>{body(t)}</button>
      ); })}
      {hidden.length > 0 && (
        <Menu>
          <MenuTrigger asChild><TabTrigger>Ещё</TabTrigger></MenuTrigger>
          <MenuContent>{hidden.map((k) => <MenuItem key={k} onSelect={() => onChange(k)}>{byKey.get(k)!.label}</MenuItem>)}</MenuContent>
        </Menu>
      )}
      {children && <span className={s.tabsAfter} data-tabs-after="">{children}</span>}
      {folds && (
        <div ref={probe} className={s.tabsProbe} aria-hidden="true">
          {items.map((t) => <span key={t.key} className={s.tab}>{body(t)}</span>)}
          <TabTrigger tabIndex={-1}>Ещё</TabTrigger>
        </div>
      )}
      {mark.shown && <span className={cx(s.tabMark, mark.moving && s.markMoving)} style={mark.style} aria-hidden="true" data-mark="" />}
    </div>
  );
}
